import { useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const SUBSCRIPTION_CACHE_KEY = ['subscription-cache'] as const;

export interface GlobalSubscription {
  plan_type: string;
  subscribed: boolean;
  is_manual_subscription: boolean;
}

const fetchSubscription = async (userId: string): Promise<GlobalSubscription> => {
  console.log('[useGlobalSubscription] Fetching subscription for user:', userId);
  
  const { data, error } = await supabase
    .from('subscriptions')
    .select('plan_type, status, is_manual_subscription')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('[useGlobalSubscription] Error fetching subscription:', error);
    throw new Error(`Subscription-Daten konnten nicht geladen werden: ${error.message}`);
  }

  const result = {
    plan_type: data?.plan_type || 'none',
    subscribed: data?.status === 'active',
    is_manual_subscription: data?.is_manual_subscription || false
  };

  console.log('[useGlobalSubscription] Subscription loaded:', result);
  return result;
};

// Prefetch-Funktion für AuthContext - wird nach Login aufgerufen
export const prefetchSubscription = async (queryClient: QueryClient, userId: string) => {
  console.log('[useGlobalSubscription] Pre-fetching subscription for user:', userId);
  
  await queryClient.prefetchQuery({
    queryKey: [...SUBSCRIPTION_CACHE_KEY, userId],
    queryFn: () => fetchSubscription(userId),
    staleTime: 10 * 60 * 1000, // 10 Minuten
  });
};

// Invalidate-Funktion für externe Komponenten
export const invalidateSubscriptionCache = (queryClient: QueryClient, userId?: string) => {
  console.log('[useGlobalSubscription] Invalidating subscription cache');
  
  if (userId) {
    queryClient.invalidateQueries({ queryKey: [...SUBSCRIPTION_CACHE_KEY, userId] });
  } else {
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_CACHE_KEY });
  }
};

export const useGlobalSubscription = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: [...SUBSCRIPTION_CACHE_KEY, user?.id],
    queryFn: () => fetchSubscription(user!.id),
    enabled: !!user,
    staleTime: 10 * 60 * 1000,  // 10 Minuten - Daten gelten als frisch
    gcTime: 30 * 60 * 1000,     // 30 Minuten im Cache behalten
  });

  const defaultSubscription: GlobalSubscription = {
    plan_type: 'none',
    subscribed: false,
    is_manual_subscription: false
  };

  // Derived state für einfache Kundentyp-Prüfungen
  const subscription = data ?? defaultSubscription;
  const isCompanyUser = subscription.plan_type?.startsWith('company_') ?? false;
  const isPrivateUser = subscription.subscribed && !isCompanyUser;

  // Invalidate-Funktion für diese User-Session
  const invalidate = () => {
    if (user?.id) {
      invalidateSubscriptionCache(queryClient, user.id);
    }
  };

  return {
    subscription,
    loading: isLoading,
    error,
    isCompanyUser,
    isPrivateUser,
    invalidate
  };
};
